import React, { useMemo } from 'react'
import { Animated, StyleSheet, View } from 'react-native'
import { buildLogoScene, sampleSprite, type LogoScene, type SampledTrack, type SceneLayout } from '../../../lib/logoEggScene'
import type { LogoEnemy, LogoReaction } from '../../../lib/logoEasterEgg'
import { SpriteArt } from './LogoEggSprites'

function track(progress: Animated.Value, value: SampledTrack | number) {
  return typeof value === 'number' ? value : progress.interpolate({ inputRange: value.input, outputRange: value.output })
}

function rotation(progress: Animated.Value, value: SampledTrack | number) {
  return typeof value === 'number' ? `${value}deg` : progress.interpolate({ inputRange: value.input, outputRange: value.output.map(v => `${v}deg`) })
}

/**
 * Draws a scene. `progress` (0..1) drives every sprite through interpolation
 * tables sampled from the scene, so a native-driver timing runs it off the JS
 * thread; a fixed value renders one frame (previews and screenshots).
 */
export function LogoEggStage({ scene, progress, variant }: { scene: LogoScene; progress: Animated.Value; variant: 'light' | 'dark' }) {
  const sprites = useMemo(() => scene.sprites.map(sprite => {
    const t = sampleSprite(sprite, scene.duration)
    return {
      sprite,
      style: {
        position: 'absolute' as const, left: -sprite.width / 2, top: -sprite.height / 2, width: sprite.width, height: sprite.height,
        opacity: track(progress, t.opacity),
        transform: [{ translateX: track(progress, t.x) }, { translateY: track(progress, t.y) }, { rotate: rotation(progress, t.rotate) },
          { scaleX: track(progress, t.scaleX) }, { scaleY: track(progress, t.scaleY) }],
      },
    }
  }), [scene, progress])
  return <View pointerEvents="none" style={StyleSheet.absoluteFill} testID={`logo-egg-stage-${scene.reaction}`}>
    {sprites.map(({ sprite, style }) => <Animated.View key={sprite.id} pointerEvents="none" style={style}>
      <SpriteArt kind={sprite.kind} width={sprite.width} height={sprite.height} variant={variant} />
    </Animated.View>)}
  </View>
}

/** A single still of a reaction, for the preview film strips and visual baselines. */
export function LogoEggFrame({ reaction, enemies, layout, frame, variant, reducedMotion = false, seed = 0 }: {
  reaction: LogoReaction; enemies?: readonly LogoEnemy[]; layout: SceneLayout; frame: number; variant: 'light' | 'dark'; reducedMotion?: boolean; seed?: number
}) {
  const scene = useMemo(() => buildLogoScene(reaction, layout, { enemies, reducedMotion, seed }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- layout/enemies are compared by value
    [reaction, reducedMotion, seed, JSON.stringify(layout), (enemies ?? []).join()])
  const progress = useMemo(() => new Animated.Value(frame), [frame])
  return <LogoEggStage scene={scene} progress={progress} variant={variant} />
}
